import { motion } from "framer-motion";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { QUOTE_DOC, QUOTE_ANALYSIS } from "../../lib/product";
import { SectionIntro, STATUS_TONES, DemoChip } from "./shared";

const QuoteAnalyzerSection = () => (
    <section
        id="quote-analyzer"
        data-testid="quote-analyzer-section"
        className="relative bg-ink py-24 lg:py-32"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <SectionIntro
                testid="quote-intro"
                eyebrow="QUOTE ANALYZER"
                title="Know what you're actually being quoted."
            />

            <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:gap-12">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="quote-document"
                    className="border border-white/12 bg-[#0C1428]"
                >
                    <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                        <div>
                            <p className="text-[15px] font-semibold text-white">
                                {QUOTE_DOC.contractor}
                            </p>
                            <p className="mt-1 font-mono text-[13px] tracking-[0.2em] text-cad">
                                {QUOTE_DOC.meta}
                            </p>
                        </div>
                        <DemoChip>EXAMPLE DOCUMENT</DemoChip>
                    </div>
                    <div className="px-6 py-2">
                        {QUOTE_DOC.rows.map((row) => (
                            <div
                                key={row.item}
                                className="flex items-center justify-between border-b border-white/[0.06] py-4"
                            >
                                <div>
                                    <p className="text-[15px] text-white/90">
                                        {row.item}
                                    </p>
                                    <p className="mt-0.5 font-mono text-[13px] tracking-[0.18em] text-cad">
                                        {row.detail}
                                    </p>
                                </div>
                                <p className="font-mono text-[14px] text-white/90">
                                    {row.amount}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 px-6 py-5">
                        <p className="font-mono text-[13px] tracking-[0.22em] text-mist">
                            TOTAL (EXCL. VAT)
                        </p>
                        <p className="font-display text-2xl font-semibold text-white">
                            {QUOTE_DOC.total}
                        </p>
                    </div>
                    <p className="border-t border-white/10 px-6 py-4 font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                        ILLUSTRATIVE FIGURES — SAMPLE DOCUMENT FOR PRODUCT
                        PRESENTATION
                    </p>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="quote-analysis"
                >
                    <div className="border border-white/12 bg-[#0C1428]">
                        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                            <p className="font-mono text-[13px] tracking-[0.22em] text-white">
                                BUILDEASE ANALYSIS
                            </p>
                            <DemoChip>DEMO</DemoChip>
                        </div>
                        <ul className="px-6 py-2">
                            {QUOTE_ANALYSIS.map((row) => {
                                const tone = STATUS_TONES[row.tone];
                                return (
                                    <li
                                        key={row.item}
                                        data-testid={`quote-row-${row.item
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")}`}
                                        className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] py-4"
                                    >
                                        <span className="text-[15px] text-white/90">
                                            {row.item}
                                        </span>
                                        <span
                                            className={`inline-flex items-center gap-2 border px-2.5 py-1 font-mono text-[13px] tracking-[0.18em] ${tone.border} ${tone.text}`}
                                        >
                                            <span
                                                className={`h-1 w-1 rounded-full ${tone.dot}`}
                                            />
                                            {row.state}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                        <p className="border-t border-white/10 px-6 py-4 text-[13px] leading-relaxed text-mist/80">
                            Assessments help you understand the quote against
                            observed market ranges — they are not a judgement
                            of any contractor.
                        </p>
                    </div>
                    <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-mist">
                        BuildEase reads the quote line by line and shows where
                        each price sits — so you can ask better questions
                        before you sign.
                    </p>
                </motion.div>
            </div>
        </div>
    </section>
);

export default QuoteAnalyzerSection;
